import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@erp/ui';
import { Button } from '@erp/ui';
import { Input } from '@erp/ui';
import { Label } from '@erp/ui';
import { DatePicker } from '@erp/ui';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@erp/ui';
import { useCreatePayment } from '@/hooks/use-payments';
import type { PaymentMethod } from '@erp/data-access/interfaces';

const METHODS: PaymentMethod[] = ['cash', 'transfer', 'check', 'card'];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId: string;
  documentType: string;
  clientId: string;
  remainingAmount: number;
  onCreated?: () => void;
}

export function AddPaymentDialog({ open, onOpenChange, documentId, documentType, clientId, remainingAmount, onCreated }: Props) {
  const { t } = useTranslation();
  const createPayment = useCreatePayment();
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [amount, setAmount] = useState(remainingAmount);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (amount <= 0) return;
    setError(null);
    try {
      await createPayment.mutateAsync({
        paymentType: 'receipt',
        paymentMethod: method,
        thirdPartyType: 'client',
        thirdPartyId: clientId,
        amount,
        paymentDate: date,
        allocations: [{ documentType, documentId, allocatedAmount: amount }],
      });
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err);
      // Si c'est une clé i18n connue (ex: 'caisse.errors.xxx'), la traduire.
      setError(raw.startsWith('caisse.') ? t(raw) : raw);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('payments.addPayment')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t('payments.method')}</Label>
            <Select value={method} onValueChange={(v) => setMethod(v as PaymentMethod)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {METHODS.map((m) => (
                  <SelectItem key={m} value={m}>{t(`payments.methods.${m}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{t('payments.amount')}</Label>
            <Input type="number" step="0.001" value={amount} onChange={(e) => setAmount(Number(e.target.value))} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('payments.date')}</Label>
            <DatePicker value={date} onChange={setDate} />
          </div>
          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button type="button" onClick={handleSubmit} disabled={createPayment.isPending || amount <= 0}>
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
